import { useState, useCallback, useRef } from 'react'
import { parseStreamEvent } from './sseEvents'
import { RETAILER_AGENT_BASE_URL, getSessionToken } from './config'

const STORED_SESSION_KEY_PREFIX = 'retailer_agent_session_id'

function writeStoredSessionId(partnerId: string, sessionId: string): void {
  try {
    localStorage.setItem(`${STORED_SESSION_KEY_PREFIX}_${partnerId}`, sessionId)
  } catch {
    // ignore storage errors
  }
}

function clearStoredSessionId(partnerId: string): void {
  try {
    localStorage.removeItem(`${STORED_SESSION_KEY_PREFIX}_${partnerId}`)
  } catch {
    // ignore storage errors
  }
}

function readStoredSessionId(partnerId: string): string | null {
  try {
    return localStorage.getItem(`${STORED_SESSION_KEY_PREFIX}_${partnerId}`)
  } catch {
    return null
  }
}

export type MessageRole = 'user' | 'assistant'

export interface MutationPreview {
  kind: 'mutation_preview'
  toolName: string
  idempotent: boolean
  beforeState: Record<string, unknown>
  proposedChanges: Record<string, unknown>
  summary?: string // fallback text from mesh content[0].text
}

export type ChatMessage =
  | { id: string; role: 'user' | 'assistant'; content: string; toolName?: string }
  | {
      id: string
      role: 'approval'
      preview: MutationPreview
      status: 'pending' | 'approved' | 'cancelled'
      settledAt?: number
      suspensionToken: string // hook-internal, not for render
      locked?: boolean
    }

export interface RetailerAgentChatState {
  messages: ChatMessage[]
  isStreaming: boolean
  isToolRunning: boolean
  isRestoringHistory: boolean
  toolName: string | null
  error: string | null
  hasPendingApproval: boolean
  sendMessage: (message: string, partnerId: string, allowedTools?: string[]) => Promise<void>
  confirmApproval: (partnerId: string) => Promise<void>
  cancelApproval: (partnerId: string) => void
  stopStreaming: () => void
  startNewChat: (partnerId: string) => void
  switchToSession: (sessionId: string, partnerId: string) => Promise<void>
  readStoredSessionId: (partnerId: string) => string | null
}

export function useRetailerAgentChat(): RetailerAgentChatState {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [isToolRunning, setIsToolRunning] = useState(false)
  const [isRestoringHistory, setIsRestoringHistory] = useState(false)
  const [toolName, setToolName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Mirror of `messages` for callbacks that need to read the latest value without
  // taking it as a dependency (avoids stale-closure issues in confirmApproval).
  const messagesRef = useRef<ChatMessage[]>([])

  const updateMessages = useCallback((updater: (prev: ChatMessage[]) => ChatMessage[]) => {
    setMessages(prev => {
      const next = updater(prev)
      messagesRef.current = next
      return next
    })
  }, [])

  const appendAssistantChunk = useCallback(
    (chunk: string) => {
      updateMessages(prev => {
        const last = prev[prev.length - 1]
        if (last?.role === 'assistant') {
          return [...prev.slice(0, -1), { ...last, content: last.content + chunk }]
        }
        return [...prev, { id: crypto.randomUUID(), role: 'assistant', content: chunk }]
      })
    },
    [updateMessages]
  )

  const runStream = useCallback(
    async (url: string, body: Record<string, unknown>, partnerId: string) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setIsStreaming(true)
      setError(null)

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getSessionToken() ?? ''}`,
            'X-IPP-Partner-Ids': partnerId,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        })

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          setError(`Request failed (${response.status}): ${text}`)
          return
        }

        const reader = response.body?.getReader()
        if (!reader) {
          setError('No response body')
          return
        }

        const decoder = new TextDecoder()
        let buffer = ''

        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''

            for (const line of lines) {
              const trimmed = line.trim()
              if (!trimmed.startsWith('data: ')) continue
              const data = trimmed.slice(6)
              if (data === '[DONE]') break

              let json: unknown
              try {
                json = JSON.parse(data)
              } catch {
                continue
              }

              const event = parseStreamEvent(json)
              if (!event) continue

              if ('type' in event) {
                switch (event.type) {
                  case 'session_created':
                    sessionIdRef.current = event.session_id
                    writeStoredSessionId(partnerId, event.session_id)
                    break
                  case 'text_delta':
                    appendAssistantChunk(event.text)
                    break
                  case 'tool_completed':
                    setIsToolRunning(false)
                    setToolName(null)
                    updateMessages(prev => {
                      // Walk newest→oldest for the latest pending approval.
                      for (let i = prev.length - 1; i >= 0; i--) {
                        const m = prev[i]
                        if (m.role === 'approval' && m.status === 'pending') {
                          const updated = [...prev]
                          updated[i] = {
                            ...m,
                            status: 'approved',
                            settledAt: Date.now(),
                            locked: false,
                          }
                          return updated
                        }
                      }
                      return prev
                    })
                    break
                  case 'approval_required':
                    updateMessages(prev => [
                      ...prev,
                      {
                        id: crypto.randomUUID(),
                        role: 'approval',
                        preview: event.preview,
                        status: 'pending',
                        suspensionToken: event.suspension_token,
                      },
                    ])
                    break
                  case 'provider_error':
                    setError(event.message ?? 'An error occurred')
                    break
                }
              } else if (event.partial === true) {
                // ADK native streaming event
                for (const part of event.content?.parts ?? []) {
                  if (part.text) {
                    appendAssistantChunk(part.text)
                  } else if (part.function_call) {
                    setIsToolRunning(true)
                    setToolName(part.function_call.name)
                  } else if (part.function_response) {
                    setIsToolRunning(false)
                    setToolName(null)
                  }
                }
              }
            }
          }
        } finally {
          reader.releaseLock()
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError((err as Error).message)
        }
      } finally {
        setIsStreaming(false)
        setIsToolRunning(false)
        setToolName(null)
      }
    },
    [appendAssistantChunk, updateMessages]
  )

  const sendMessage = useCallback(
    async (message: string, partnerId: string, allowedTools: string[] = []) => {
      updateMessages(prev => [...prev, { id: crypto.randomUUID(), role: 'user', content: message }])

      await runStream(
        `${RETAILER_AGENT_BASE_URL}/stream`,
        {
          message,
          allowed_tools: allowedTools,
          session_id: sessionIdRef.current,
          partner_id: partnerId,
        },
        partnerId
      )
    },
    [runStream, updateMessages]
  )

  const confirmApproval = useCallback(
    async (partnerId: string) => {
      // Derive the pending approval from the current messages array (via ref so we
      // don't have to take `messages` as a dependency).
      const pending = messagesRef.current.find(m => m.role === 'approval' && m.status === 'pending')
      if (!pending || pending.role !== 'approval' || !sessionIdRef.current) return

      // Optimistic lock so a double-click can't fire two /resume requests. The
      // tool_completed event from the resume stream clears `locked` and flips
      // status to 'approved' (see runStream tool_completed handler).
      updateMessages(prev =>
        prev.map(m => (m.id === pending.id && m.role === 'approval' ? { ...m, locked: true } : m))
      )

      const sid = sessionIdRef.current
      try {
        await runStream(
          `${RETAILER_AGENT_BASE_URL}/resume`,
          {
            session_id: sid,
            suspension_token: pending.suspensionToken,
            approved: true,
            tool_name: pending.preview.toolName,
          },
          partnerId
        )
      } finally {
        // If the resume stream ended without a tool_completed event (network
        // failure, provider_error, etc.) the card is still locked+pending,
        // leaving the composer permanently disabled with no way to retry or
        // cancel. Unlock any message that is still pending so the user can act.
        updateMessages(prev =>
          prev.map(m =>
            m.id === pending.id && m.role === 'approval' && m.status === 'pending'
              ? { ...m, locked: false }
              : m
          )
        )
      }
    },
    [runStream, updateMessages]
  )

  const cancelApproval = useCallback(
    (partnerId: string) => {
      const pending = messagesRef.current.find(m => m.role === 'approval' && m.status === 'pending')
      if (!pending || pending.role !== 'approval') return

      // Flip to cancelled immediately so the UI updates without waiting on the network.
      updateMessages(prev =>
        prev.map(m =>
          m.id === pending.id && m.role === 'approval'
            ? { ...m, status: 'cancelled' as const, settledAt: Date.now() }
            : m
        )
      )

      // Fire-and-forget cleanup. The orchestrator's stream-top defensive cleanup is
      // the backstop if this fails (Task 8 already shipped that).
      if (sessionIdRef.current) {
        void fetch(`${RETAILER_AGENT_BASE_URL}/sessions/${sessionIdRef.current}/cancel-pending`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getSessionToken() ?? ''}`,
            'X-IPP-Partner-Ids': partnerId,
          },
        }).catch(() => {
          // Non-fatal — the /stream defensive cleanup will reap this on the next turn.
        })
      }
    },
    [updateMessages]
  )

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  // Fire-and-forget POST to cancel any pending approval on a prior session before
  // we discard or swap it. Used by startNewChat and switchToSession so the mesh
  // Redis entry is reaped without blocking the UI.
  const cancelPendingForPriorSession = useCallback((priorSessionId: string, partnerId: string) => {
    void fetch(`${RETAILER_AGENT_BASE_URL}/sessions/${priorSessionId}/cancel-pending`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getSessionToken() ?? ''}`,
        'X-IPP-Partner-Ids': partnerId,
      },
    }).catch(() => {
      // Non-fatal — defensive /stream cleanup is the backstop.
    })
  }, [])

  const startNewChat = useCallback(
    (partnerId: string) => {
      const prior = sessionIdRef.current
      if (prior) cancelPendingForPriorSession(prior, partnerId)
      sessionIdRef.current = null
      clearStoredSessionId(partnerId)
      updateMessages(() => [])
      setError(null)
    },
    [cancelPendingForPriorSession, updateMessages]
  )

  const switchToSession = useCallback(
    async (sessionId: string, partnerId: string) => {
      const prior = sessionIdRef.current
      if (prior && prior !== sessionId) cancelPendingForPriorSession(prior, partnerId)
      setIsRestoringHistory(true)
      try {
        const response = await fetch(`${RETAILER_AGENT_BASE_URL}/sessions/${sessionId}/messages`, {
          headers: {
            Authorization: `Bearer ${getSessionToken() ?? ''}`,
            'X-IPP-Partner-Ids': partnerId,
          },
        })

        if (!response.ok) return

        const data = (await response.json()) as {
          messages: Array<{ role: string; content: string }>
        }

        const restoredMessages: ChatMessage[] = (data.messages ?? []).map(m => ({
          id: crypto.randomUUID(),
          role: m.role === 'user' ? ('user' as const) : ('assistant' as const),
          content: m.content,
        }))

        sessionIdRef.current = sessionId
        writeStoredSessionId(partnerId, sessionId)
        updateMessages(() => restoredMessages)
        setError(null)
      } catch {
        // silently fail — user can still chat normally
      } finally {
        setIsRestoringHistory(false)
      }
    },
    [cancelPendingForPriorSession, updateMessages]
  )

  return {
    messages,
    isStreaming,
    isToolRunning,
    isRestoringHistory,
    toolName,
    error,
    hasPendingApproval: messages.some(m => m.role === 'approval' && m.status === 'pending'),
    sendMessage,
    confirmApproval,
    cancelApproval,
    stopStreaming,
    startNewChat,
    switchToSession,
    readStoredSessionId,
  }
}
